// Handgeschreven, getypeerde client voor het S1-contract (docs/CONTRACT-S1.md), bij de merge van
// sprint 1 gelijkgetrokken met de echte API (apps/api/openapi.json, `pnpm --filter @amumin/api
// openapi`). TODO(S2): genereren uit openapi.json met dezelfde vorm.
// Kiosk-veilig: geen Zod-runtime, alleen fetch + JSON. Cookies (fb_sess/fb_chal/fb_trust/fb_dev)
// gaan mee via `credentials: 'same-origin'`; elke app spreekt alleen zijn eigen origin aan
// (ONTWERP.md §3.1). Alle fouten hebben de vorm {error: {code, message, details?}}.
import { createFoodApi, createKioskFoodApi } from './food.js';
import { createProfileApi } from './profile.js';
export * from './food.js';
import { createFamilyClient } from './family.js';
import { createWeatherClient } from './weather.js';
import { createActivityClient, } from './activity.js';
export * from './activity.js';
import { createGrowthClient, createSchoolClient } from './groeiSchool.js';
import { createSchoolPlanClient } from './schoolPlan.js';
import { createMemorizeClient, } from './memorize.js';
export * from './memorize.js';
import { createSamenClient } from './samen.js';
export * from './samen.js';
import { createCareClient, createHouseInviteClient } from './care.js';
export * from './care.js';
export * from './groeiSchool.js';
// 0076: schoolplanner.
export * from './schoolPlan.js';
import { createMapsClient } from './maps.js';
export * from './maps.js';
import { createPushClient } from './push.js';
export * from './push.js';
import { createUiClient } from './ui.js';
export * from './ui.js';
import { createNotesClient } from './notes.js';
export * from './notes.js';
import { createHuisrondeClient } from './huisronde.js';
export * from './huisronde.js';
import { createMoneyClient } from './money.js';
export * from './money.js';
import { createPhotoFrameClient } from './photoFrame.js';
import { createKompasClient } from './kompas.js';
export * from './kompas.js';
export * from './photoFrame.js';
import { createBillingClient, createSignupClient } from './billing.js';
export * from './billing.js';
import { createPlatformClient } from './platform.js';
import { createHouseholdsClient, } from './households.js';
export * from './households.js';
import { createPrivacyClient } from './privacy.js';
export * from './family.js';
export * from './weather.js';
/** Leest `error.code` uit een (onbekende) foutbody; `undefined` als de vorm niet klopt. */
export function errorCodeOf(body) {
    if (!body || typeof body !== 'object')
        return undefined;
    const err = body.error;
    if (!err || typeof err !== 'object')
        return undefined;
    const code = err.code;
    return typeof code === 'string' ? code : undefined;
}
export class ApiError extends Error {
    constructor(status, body) {
        var _a;
        super(`API ${status}`);
        this.status = status;
        this.body = body;
        this.name = 'ApiError';
        this.code = (_a = errorCodeOf(body)) !== null && _a !== void 0 ? _a : `http_${status}`;
    }
    /** `error.details` (bv. `{current}` bij 409 version_conflict, `{reason}` bij 400). */
    get details() {
        if (!this.body || typeof this.body !== 'object')
            return undefined;
        const err = this.body.error;
        return err && typeof err === 'object' ? err.details : undefined;
    }
}
function query(params) {
    const parts = [];
    for (const key of Object.keys(params)) {
        const value = params[key];
        if (value === undefined)
            continue;
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
    return parts.length > 0 ? `?${parts.join('&')}` : '';
}
export function createApiClient(options = {}) {
    var _a, _b;
    const baseUrl = (_a = options.baseUrl) !== null && _a !== void 0 ? _a : '';
    const doFetch = (_b = options.fetch) !== null && _b !== void 0 ? _b : fetch;
    async function request(method, path, body, extraHeaders) {
        const init = {
            method,
            credentials: 'same-origin',
            headers: {
                ...(body === undefined
                    ? { Accept: 'application/json' }
                    : { Accept: 'application/json', 'Content-Type': 'application/json' }),
                ...extraHeaders,
            },
        };
        if (body !== undefined)
            init.body = JSON.stringify(body);
        const res = await doFetch(baseUrl + path, init);
        const text = await res.text();
        let json = null;
        if (text.length > 0) {
            try {
                json = JSON.parse(text);
            }
            catch {
                json = { error: { code: 'invalid_json', message: text.slice(0, 200) } };
            }
        }
        if (!res.ok)
            throw new ApiError(res.status, json);
        return json;
    }
    const get = (path) => request('GET', path);
    const post = (path, body = {}) => request('POST', path, body);
    const patch = (path, body) => request('PATCH', path, body);
    const del = (path, body) => request('DELETE', path, body);
    const id = (value) => encodeURIComponent(value);
    return {
        health: () => get('/healthz'),
        /** w4 familie: wensen, deelpagina, kalenders, koppelingen, berichten (family.ts). */
        family: createFamilyClient({ request, baseUrl }),
        /** 0025-0026: locatie en weer (weather.ts). */
        weather: createWeatherClient({ request }),
        /** 0031 (golf 2): vaste activiteiten met pauzes (activity.ts). */
        activities: createActivityClient({ request }),
        /** 0032 (golf 3): Groei (module Gezondheid) en School (groeiSchool.ts). */
        growth: createGrowthClient({ request }),
        school: createSchoolClient({ request }),
        /** 0076: schoolplanner (schoolPlan.ts). */
        schoolPlan: createSchoolPlanClient({ request }),
        /** 0048 (golf 7): Soera's leren (memorize.ts). */
        memorize: createMemorizeClient({ request }),
        /** 0049 (golf 8): Wie eet mee, Reserveren, Kosten delen, inrichten (samen.ts). */
        samen: createSamenClient({ request }, baseUrl),
        /** 0050 (golf 8): dagelijkse check-in en familiekring (care.ts). */
        care: createCareClient({ request }),
        /** Golf 8 ronde 1 (ON-3): huisgenoten per e-mail uitnodigen. */
        houseInvites: createHouseInviteClient({ request }),
        /** 0034 (golf 3): plaatsen zoeken, reistijd, agenda koppelen (maps.ts). */
        maps: createMapsClient({ request }),
        /** Platformbeheerder: Werklijst (platform.ts); voor ieder ander 404. */
        platform: createPlatformClient({ request }),
        /** 0036: privacyrechten (ZIP-export, iemand uit het gezin halen, gezinsbord verwijderen). */
        privacy: createPrivacyClient({ request }),
        /** 0038: meldingen op je telefoon (push.ts). */
        push: createPushClient({ request }),
        /** 0042 (golf 6b): eigen schermkeuzes, indeling van Vandaag en agendaweergave (ui.ts). */
        ui: createUiClient({ request }),
        /** 0060: notities (notes.ts). */
        notes: createNotesClient({ request, baseUrl }),
        /** 0075: Huisronde (huisronde.ts; module Eten). */
        huisronde: createHuisrondeClient({ request }),
        /** 0064: Fotolijst (photoFrame.ts; alleen ouders). */
        photoFrame: createPhotoFrameClient({ request, baseUrl }),
        /** 0068: Gezond op gewicht (kompas.ts; alleen voor jezelf). */
        kompas: createKompasClient({ request, baseUrl }),
        /** 0069: Geldzaken (money.ts; alleen de volwassene zelf, in zijn eigen omgeving). */
        money: createMoneyClient({ request, baseUrl }),
        /** 0065: aanmelden (zonder sessie) en Instellingen › Abonnement (billing.ts). */
        signup: createSignupClient({ request }),
        billing: createBillingClient({ request }),
        /** Golf 6b (0043): soort huishouden en beurtrooster (households.ts). */
        households: createHouseholdsClient({ request }),
        /** Ouderapp (cookie fb_sess). */
        app: {
            auth: {
                register: (input) => post('/v1/app/auth/register', input),
                verify: (token) => post('/v1/app/auth/verify', { token }),
                login: (input) => post('/v1/app/auth/login', input),
                challengeVerify: (input) => post('/v1/app/auth/challenge/verify', input),
                challengeResend: () => post('/v1/app/auth/challenge/resend', {}),
                logout: (forgetBrowser = false) => post('/v1/app/auth/logout', {
                    forget_browser: forgetBrowser,
                }),
                logoutEverywhere: () => post('/v1/app/auth/logout-everywhere', {}),
                resetRequest: (email) => post('/v1/app/auth/reset/request', { email }),
                resetConfirm: (token, password) => post('/v1/app/auth/reset/confirm', { token, password }),
                stepUp: (password) => post('/v1/app/auth/step-up', { password }),
                context: () => get('/v1/app/auth/context'),
                personalSpace: () => post('/v1/app/auth/personal-space'),
                preferences: () => get('/v1/app/auth/preferences'),
                setPreferences: (theme) => request('PUT', '/v1/app/auth/preferences', { theme }),
                /** Landingspagina zonder bijwerking: wat gaat "Dit was ik niet" doen (contract-aanvulling). */
                notMePreview: (token) => post('/v1/app/auth/not-me/preview', { token }),
                notMe: (token) => post('/v1/app/auth/not-me', { token }),
            },
            calendarSync: {
                list: () => get('/v1/app/calendar-sync'),
                connect: (input) => post('/v1/app/calendar-sync/connect', input),
                sync: (id, resolution) => post(`/v1/app/calendar-sync/${id}/sync`, resolution ? { resolution } : {}),
                disconnect: (id) => request('DELETE', `/v1/app/calendar-sync/${id}`),
            },
            security: {
                trustedNetworks: {
                    list: () => get('/v1/app/security/trusted-networks'),
                    current: () => post('/v1/app/security/trusted-networks/current', {}),
                    create: (input) => post('/v1/app/security/trusted-networks', input),
                    update: (networkId, input) => patch(`/v1/app/security/trusted-networks/${id(networkId)}`, input),
                    remove: (networkId) => del(`/v1/app/security/trusted-networks/${id(networkId)}`),
                },
                trustedBrowsers: {
                    list: () => get('/v1/app/security/trusted-browsers'),
                    remove: (browserId) => del(`/v1/app/security/trusted-browsers/${id(browserId)}`),
                    revokeOthers: () => post('/v1/app/security/trusted-browsers/revoke-others', {}),
                },
                /** Sessielijst (ONTWERP.md §11.1.7; contract-aanvulling op de S1-tekst). */
                sessions: {
                    list: () => get('/v1/app/security/sessions'),
                    remove: (sessionId) => del(`/v1/app/security/sessions/${id(sessionId)}`),
                },
                /** Eigen ouder-PIN (4–6 cijfers, step-up). */
                setPin: (pin) => request('PUT', '/v1/app/security/pin', { pin }),
            },
            /** 0029: modules van het gezin (aan/uit, opties; wist nooit iets). */
            modules: {
                list: () => get('/v1/app/modules'),
                update: (moduleId, input) => patch(`/v1/app/modules/${id(moduleId)}`, input),
            },
            household: {
                get: () => get('/v1/app/household'),
                /** Naam wijzigen en/of de wizard afronden (daarna geldt step-up strikt). */
                update: (input) => patch('/v1/app/household', input),
                switch: (householdId) => post('/v1/app/household/switch', {
                    household_id: householdId,
                }),
            },
            members: {
                list: () => get('/v1/app/members'),
                create: (input) => post('/v1/app/members', input),
                update: (memberId, input) => patch(`/v1/app/members/${id(memberId)}`, input),
                /** Volgorde van het gezin: alle actieve leden in de nieuwe volgorde (409 = opnieuw laden). */
                reorder: (memberIds) => request('PUT', '/v1/app/members/order', {
                    member_ids: memberIds,
                }),
            },
            pair: {
                inspect: (userCode) => get(`/v1/app/pair/${id(userCode)}`),
                approve: (input) => post('/v1/app/pair/approve', input),
            },
            devices: {
                list: () => get('/v1/app/devices'),
                update: (deviceId, input) => patch(`/v1/app/devices/${id(deviceId)}`, input),
                revoke: (deviceId) => post(`/v1/app/devices/${id(deviceId)}/revoke`, {}),
                /** Menu Board: meekijken met dit bord (alleen ouders, alleen lezen). */
                view: (deviceId) => post(`/v1/app/devices/${id(deviceId)}/view`, {}),
                /** 0040: koppelen met een QR-code (step-up; eenmalig, 10 minuten, één kamer). */
                qrGrants: {
                    create: (input) => post('/v1/app/devices/qr-grants', input),
                    state: (grantId) => get(`/v1/app/devices/qr-grants/${id(grantId)}`),
                    cancel: (grantId) => del(`/v1/app/devices/qr-grants/${id(grantId)}`),
                },
            },
            /** 0028: Virtuele iPad (ouders; koppelen met step-up, kamer en intrekken via devices). */
            virtualDevice: {
                get: () => get('/v1/app/virtual-device'),
                pair: (input) => post('/v1/app/virtual-device/pair', input),
            },
            agenda: {
                get: (from, to) => get(`/v1/app/agenda${query({ from, to })}`),
                /** 48 u-widget "wie rijdt?" (CAL-26). */
                rides: (hours) => get(`/v1/app/agenda/rides${query({ hours })}`),
            },
            events: {
                create: (input) => post('/v1/app/events', input),
                /** NL-samenvatting, eerstvolgende datums, conflicten en waarschuwingen vóór opslaan. */
                preview: (input) => post('/v1/app/events/preview', input),
                get: (eventId) => get(`/v1/app/events/${id(eventId)}`),
                history: (eventId) => get(`/v1/app/events/${id(eventId)}/history`),
                /** Scope 'all' (standaard), 'this_and_following' of 'this' (met occurrence_id). */
                update: (eventId, input) => patch(`/v1/app/events/${id(eventId)}`, input),
                remove: (eventId, ifVersion, scope) => del(`/v1/app/events/${id(eventId)}`, ifVersion === undefined && scope === undefined
                    ? undefined
                    : { ...(ifVersion === undefined ? {} : { if_version: ifVersion }), ...(scope !== null && scope !== void 0 ? scope : {}) }),
                trash: () => get('/v1/app/events/trash'),
                restore: (eventId, ifVersion) => post(`/v1/app/events/${id(eventId)}/restore`, ifVersion === undefined ? {} : { if_version: ifVersion }),
                duplicate: (eventId, date) => post(`/v1/app/events/${id(eventId)}/duplicate`, date === undefined ? {} : { date }),
                fromTemplate: (input) => post('/v1/app/events/from-template', input),
                /** Download-URL's (ICS, RFC 5545). */
                icsHouseholdUrl: () => `${baseUrl}/v1/app/ics/household.ics`,
                icsMemberUrl: (memberId) => `${baseUrl}/v1/app/ics/members/${id(memberId)}`,
            },
            /** "Alleen deze" (override); occurrenceId = `<root_series_id>:<original_start_local>`. */
            occurrences: {
                update: (occurrenceId, input) => patch(`/v1/app/occurrences/${id(occurrenceId)}`, input),
                move: (occurrenceId, input) => post(`/v1/app/occurrences/${id(occurrenceId)}/move`, input),
                cancel: (occurrenceId, input = {}) => post(`/v1/app/occurrences/${id(occurrenceId)}/cancel`, input),
                restore: (occurrenceId, ifVersion) => post(`/v1/app/occurrences/${id(occurrenceId)}/restore`, ifVersion === undefined ? {} : { if_version: ifVersion }),
                reset: (occurrenceId, ifVersion) => post(`/v1/app/occurrences/${id(occurrenceId)}/reset`, ifVersion === undefined ? {} : { if_version: ifVersion }),
                setDriver: (occurrenceId, input) => post(`/v1/app/occurrences/${id(occurrenceId)}/driver`, input),
            },
            eventTemplates: {
                /** 0024: ingebouwde sjablonen volgens het gezinsprofiel (verborgen met reden, teller). */
                list: () => get('/v1/app/event-templates'),
                create: (input) => post('/v1/app/event-templates', input),
                remove: (templateId) => del(`/v1/app/event-templates/${id(templateId)}`),
            },
            tasks: {
                create: (input) => post('/v1/app/tasks', input),
                update: (taskId, input) => patch(`/v1/app/tasks/${id(taskId)}`, input),
                remove: (taskId, ifVersion) => del(`/v1/app/tasks/${id(taskId)}`, ifVersion === undefined ? undefined : { if_version: ifVersion }),
                history: (params = {}) => get(`/v1/app/tasks/history${query(params)}`),
            },
            /** Sprint 3: taken met herhaling, modi en scopes (409 duplicate_warning bij een dubbele). */
            taskSeries: {
                list: () => get('/v1/app/task-series'),
                get: (seriesId) => get(`/v1/app/task-series/${id(seriesId)}`),
                create: (input) => post('/v1/app/task-series', input),
                duplicates: (input) => post('/v1/app/task-series/duplicates', input),
                update: (seriesId, input) => patch(`/v1/app/task-series/${id(seriesId)}`, input),
                remove: (seriesId, input = {}) => del(`/v1/app/task-series/${id(seriesId)}`, input),
            },
            taskTemplates: {
                list: () => get('/v1/app/task-templates'),
                create: (input) => post('/v1/app/task-templates', input),
                remove: (templateId) => del(`/v1/app/task-templates/${id(templateId)}`),
                apply: (templateId, input) => post(`/v1/app/task-templates/${id(templateId)}/apply`, input),
            },
            /** Ouderacties op (virtuele) instances: sleutel = serie + original_date (+ lid). */
            taskInstances: {
                complete: (input) => post('/v1/app/task-instances/complete', input),
                approve: (input) => post('/v1/app/task-instances/approve', input),
                reject: (input) => post('/v1/app/task-instances/reject', input),
                revert: (input) => post('/v1/app/task-instances/revert', input),
                skip: (input) => post('/v1/app/task-instances/skip', input),
                unskip: (input) => post('/v1/app/task-instances/unskip', input),
                move: (input) => post('/v1/app/task-instances/move', input),
            },
            /** Inbox "Te controleren" (badge = count). */
            approvals: () => get('/v1/app/approvals'),
            /** 0058: to-do's per lid (open, en afgerond in de laatste 30 dagen). */
            todos: {
                list: () => get('/v1/app/todos'),
                /** Mijn omgeving: open to-do's die het gezin aan jou toewees (alleen lezen). */
                elsewhere: () => get('/v1/app/todos/elsewhere'),
            },
            points: {
                overview: () => get('/v1/app/points'),
                ledger: (params = {}) => get(`/v1/app/points/ledger${query(params)}`),
                /** Step-up vereist (403 step_up_required); negatief saldo → 409 negative_balance. */
                adjust: (input) => post('/v1/app/points/adjust', input),
                /** CSV-download-URL (zelfde origin; de browser stuurt de cookies mee). */
                exportUrl: (params = {}) => `${baseUrl}/v1/app/points/export.csv${query(params)}`,
                setGoal: (memberId, rewardId) => request('PUT', `/v1/app/members/${id(memberId)}/goal`, {
                    reward_id: rewardId,
                }),
            },
            rewards: {
                list: (includeArchived = false) => get(`/v1/app/rewards${query({ include_archived: includeArchived ? 'true' : undefined })}`),
                create: (input) => post('/v1/app/rewards', input),
                update: (rewardId, input) => patch(`/v1/app/rewards/${id(rewardId)}`, input),
                archive: (rewardId, ifVersion) => del(`/v1/app/rewards/${id(rewardId)}`, ifVersion === undefined ? undefined : { if_version: ifVersion }),
            },
            /** 0023: Wat past bij ons gezin (alleen ouders; profile.ts). */
            profile: createProfileApi({ request, id }),
            /** Sprint 4: huishouden (boodschappen, recepten, weekmenu, voorraad; food.ts). */
            food: createFoodApi({ get, post, patch, del, request, id, query, baseUrl }),
            /** Sprint 4: kamers met bewoners (apparaat → kamer blijft devices.update). */
            rooms: {
                list: () => get('/v1/app/rooms'),
                create: (input) => post('/v1/app/rooms', input),
                update: (roomId, input) => patch(`/v1/app/rooms/${id(roomId)}`, input),
                remove: (roomId, ifVersion) => del(`/v1/app/rooms/${id(roomId)}`, ifVersion === undefined ? {} : { if_version: ifVersion }),
                restore: (roomId) => post(`/v1/app/rooms/${id(roomId)}/restore`, {}),
            },
            /** Sprint 4: schoonmaak (plekken, klussen, historie, loggen, tombstone). */
            cleaning: {
                overview: () => get('/v1/app/cleaning'),
                createLocation: (input) => post('/v1/app/cleaning/locations', input),
                updateLocation: (locationId, input) => patch(`/v1/app/cleaning/locations/${id(locationId)}`, input),
                archiveLocation: (locationId, ifVersion) => del(`/v1/app/cleaning/locations/${id(locationId)}`, {
                    if_version: ifVersion,
                }),
                createItem: (input) => post('/v1/app/cleaning/items', input),
                updateItem: (itemId, input) => patch(`/v1/app/cleaning/items/${id(itemId)}`, input),
                archiveItem: (itemId, ifVersion) => del(`/v1/app/cleaning/items/${id(itemId)}`, { if_version: ifVersion }),
                history: (params = {}) => get(`/v1/app/cleaning/logs${query(params)}`),
                log: (input) => post('/v1/app/cleaning/logs', input),
                tombstone: (logId, confirmNegative = false) => del(`/v1/app/cleaning/logs/${id(logId)}`, confirmNegative ? { confirm_negative: true } : {}),
            },
            /** Sprint 4: kindslot (step-up bij instellen en uitzetten). */
            /**
             * 0077: privé op de muur. Overzicht "Dit staat op de muur", bevestigen (step-up; alleen je eigen
             * gegevens, het vinkje is verplicht) en intrekken (elke ouder).
             */
            wall: {
                overview: () => get('/v1/app/wall'),
                grant: (input) => post('/v1/app/wall', input),
                revoke: (shareId) => post(`/v1/app/wall/${id(shareId)}/revoke`, {}),
                /** De eenmalige melding over de oude tegel is gelezen. */
                tileNoticeSeen: () => post('/v1/app/wall/tile-notice/seen', {}),
            },
            kidLocks: {
                list: () => get('/v1/app/kid-locks'),
                set: (memberId, kind, secret) => request('PUT', `/v1/app/kid-locks/${id(memberId)}`, { kind, secret }),
                remove: (memberId) => del(`/v1/app/kid-locks/${id(memberId)}`),
            },
            /**
             * 0027: extra modules van het gezinsprofiel (neutrale paden; 404 als de module niet bij het
             * profiel hoort). Het contract staat in packages/domain schemas/prayer.ts (ExtrasView,
             * ExtrasPut, …); de webapp geeft die typen als T mee.
             */
            /**
             * Koran luisteren (1 okt 2026; neutraal pad): welke opnames dit gezin kan horen (AppQuranView
             * in packages/domain quran/schemas.ts). 404 zonder Islam in het profiel of zonder de module.
             */
            listen: {
                get: () => get('/v1/app/household/listen'),
            },
            /**
             * 0061: Kerk & kalender (neutraal pad "seasons"; SeasonsView, SeasonsPut en
             * SeasonsDaysResponse in packages/domain faith/christendom/schemas.ts). 404 zonder christelijk
             * geloof in het profiel.
             */
            seasons: {
                get: () => get('/v1/app/household/seasons'),
                put: (body) => request('PUT', '/v1/app/household/seasons', body),
                days: (from, to) => get(`/v1/app/household/seasons/days${query({ from, to })}`),
            },
            extras: {
                get: () => get('/v1/app/household/extras'),
                /** Opnieuw lezen terwijl de ouder op een tablet wacht (eigen pad, niet in de accesslog). */
                status: () => get('/v1/app/household/extras/status'),
                put: (body) => request('PUT', '/v1/app/household/extras', body),
                /** "Gebedstijden op het bord?": ja zet de module aan, nee bewaart alleen het antwoord. */
                answer: (answer) => post('/v1/app/household/extras/answer', { answer }),
                table: (body) => post('/v1/app/household/extras/table', body),
                confirm: (ifVersion) => post('/v1/app/household/extras/confirm', { if_version: ifVersion }),
                /** Eenmalige melding gelezen (golf 6b: overgezet naar de gezamenlijke kalender). */
                seen: () => post('/v1/app/household/extras/seen', {}),
                upload: (body) => post('/v1/app/household/extras/upload', body),
                device: (deviceId, body) => patch(`/v1/app/devices/${id(deviceId)}/extras`, body),
                test: (deviceId) => post(`/v1/app/devices/${id(deviceId)}/extras/test`, {}),
                /** 0063: module Hindoeïsme (neutraal pad; 404 zonder hindoeïsme in het profiel). */
                kalender: () => get('/v1/app/household/extras/kalender'),
                kalenderPut: (body) => request('PUT', '/v1/app/household/extras/kalender', body),
            },
            /**
             * 0062: module Jodendom (neutraal pad; JudaismView en JudaismPut in packages/domain
             * faith/jodendom/schemas.ts). GET geeft elke ouder 200 ({} zonder Jodendom in het profiel).
             */
            calendarExtras: {
                get: () => get('/v1/app/household/extras/calendar'),
                put: (body) => request('PUT', '/v1/app/household/extras/calendar', body),
                member: (memberId, body) => request('PUT', `/v1/app/household/extras/calendar/members/${id(memberId)}`, body),
            },
            rewardRequests: {
                list: (state) => get(`/v1/app/reward-requests${query({ state })}`),
                fulfil: (requestId, note) => post(`/v1/app/reward-requests/${id(requestId)}/fulfil`, note === undefined ? {} : { note }),
                decline: (requestId, note) => post(`/v1/app/reward-requests/${id(requestId)}/decline`, note === undefined ? {} : { note }),
            },
        },
        /** RFC 8628-pairing (anoniem, kiosk-origin). */
        pair: {
            start: (input = {}) => post('/v1/pair/start', input),
            poll: (deviceCode) => post('/v1/pair/poll', { device_code: deviceCode }),
            /** 0040: token uit het fragment van de QR-code (#k=…), alleen in de body. */
            claim: (input) => post('/v1/pair/claim', input),
        },
        /** Apparaat (cookie fb_dev). */
        device: {
            restore: (recoveryToken) => post('/v1/device/session/restore', {
                recovery_token: recoveryToken,
            }),
            heartbeat: (input) => post('/v1/device/heartbeat', input),
        },
        /** Kiosk (cookie fb_dev). */
        kiosk: {
            sync: (from, to) => get(`/v1/kiosk/sync${query({ from, to })}`),
            reportError: (input) => post('/v1/kiosk/errors', input),
            /**
             * Eén outbox-op (§6.2): altijd 200 met `result` (ook bij conflict), 400 alleen bij een
             * misvormd verzoek. Idempotent per client_op_id (30 d).
             */
            op: (input) => post('/v1/kiosk/ops', input),
            /** Sprint 4: Eten (menu, boodschappen); idempotent per client_op_id. */
            food: createKioskFoodApi({ get, post, id, baseUrl }),
            /** Sprint 4: kindslot openen → bewijs (60 s) voor de volgende op. */
            unlock: (memberId, secret) => post('/v1/kiosk/unlock', { member_id: memberId, secret }),
        },
    };
}
//# sourceMappingURL=index.js.map